import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const UserAuthIndicator = ({ 
  user = null, 
  onLogin, 
  onLogout, 
  onProfileClick,
  showNotifications = true,
  className = '' 
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Simulate notifications for authenticated users
    if (user) {
      const mockNotifications = [
        {
          id: 1,
          type: 'payment',
          title: 'Pago Confirmado',
          message: 'Tu pago de Bs. 50.00 ha sido procesado exitosamente',
          time: '2 min',
          read: false
        },
        {
          id: 2,
          type: 'ticket',
          title: 'Boleto Asignado',
          message: 'Tu boleto #AF-2024-001234 está listo',
          time: '5 min',
          read: false
        }
      ];
      
      setNotifications(mockNotifications);
      setUnreadCount(mockNotifications?.filter(n => !n?.read)?.length);
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (notification) => {
    // Mark as read
    setNotifications(prev => 
      prev?.map(n => n?.id === notification?.id ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    // Handle navigation based on notification type
    if (notification?.type === 'payment') {
      // Navigate to payment confirmation
      window.location.href = '/payment-confirmation';
    } else if (notification?.type === 'ticket') {
      // Navigate to ticket verification
      window.location.href = '/ticket-verification';
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'payment': return 'CreditCard';
      case 'ticket': return 'Ticket';
      case 'winner': return 'Trophy';
      default: return 'Bell';
    }
  };

  if (!user) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <Button
          variant="outline"
          size="sm"
          onClick={onLogin}
          iconName="LogIn"
          iconPosition="left"
          iconSize={14}
        >
          Iniciar Sesión
        </Button>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="flex items-center space-x-3">
        {/* Notifications */}
        {showNotifications && (
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="relative"
            >
              <Icon name="Bell" size={18} />
              {unreadCount > 0 && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-secondary text-secondary-foreground rounded-full flex items-center justify-center text-xs font-bold font-mono">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </div>
              )}
            </Button>
          </div>
        )}

        {/* User Profile */}
        <div className="flex items-center space-x-2">
          <div 
            className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center cursor-pointer hover:shadow-golden transition-premium"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <span className="text-xs font-bold text-secondary-foreground">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          
          <div className="hidden xl:block">
            <p className="text-sm font-medium text-foreground">{user?.name}</p>
            <p className="text-xs text-text-secondary">{user?.email}</p>
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-6 h-6"
          >
            <Icon name={isDropdownOpen ? "ChevronUp" : "ChevronDown"} size={14} />
          </Button>
        </div>
      </div>
      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-card rounded-xl shadow-luxury border border-border z-50 animate-slide-in-from-top">
          {/* User Info Header */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center">
                <span className="text-lg font-bold text-secondary-foreground">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div>
                <h3 className="font-body font-semibold text-card-foreground">{user?.name}</h3>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <div className="flex items-center space-x-1 mt-1">
                  <div className="w-2 h-2 bg-success rounded-full"></div>
                  <span className="text-xs text-success font-caption">Cuenta Verificada</span>
                </div>
              </div>
            </div>
          </div>

          {/* Notifications Section */}
          {showNotifications && notifications?.length > 0 && (
            <div className="p-4 border-b border-border">
              <h4 className="font-body font-medium text-card-foreground mb-3 flex items-center space-x-2">
                <Icon name="Bell" size={14} />
                <span>Notificaciones</span>
                {unreadCount > 0 && (
                  <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded-full font-mono">
                    {unreadCount}
                  </span>
                )}
              </h4>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {notifications?.slice(0, 3)?.map((notification) => (
                  <div
                    key={notification?.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-3 rounded-lg border cursor-pointer transition-premium hover:bg-muted/50 ${
                      notification?.read ? 'border-border bg-muted/20' : 'border-secondary/30 bg-secondary/5'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-1.5 rounded-lg ${
                        notification?.read ? 'bg-muted' : 'bg-secondary/20'
                      }`}>
                        <Icon 
                          name={getNotificationIcon(notification?.type)} 
                          size={12} 
                          className={notification?.read ? 'text-muted-foreground' : 'text-secondary'} 
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${
                          notification?.read ? 'text-muted-foreground' : 'text-card-foreground'
                        }`}>
                          {notification?.title}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {notification?.message}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">{notification?.time}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Menu Actions */}
          <div className="p-4 space-y-2">
            <Button
              variant="ghost"
              fullWidth
              onClick={onProfileClick}
              iconName="User"
              iconPosition="left"
              className="justify-start"
            >
              Mi Perfil
            </Button>
            
            <Button
              variant="ghost"
              fullWidth
              onClick={() => window.location.href = '/ticket-verification'}
              iconName="Search"
              iconPosition="left"
              className="justify-start"
            >
              Mis Boletos
            </Button>
            
            <Button
              variant="ghost"
              fullWidth
              onClick={() => window.location.href = '/purchase-flow'}
              iconName="CreditCard"
              iconPosition="left"
              className="justify-start"
            >
              Historial de Pagos
            </Button>
            
            <div className="pt-2 border-t border-border">
              <Button
                variant="outline"
                fullWidth
                onClick={onLogout}
                iconName="LogOut"
                iconPosition="left"
                className="justify-start text-error hover:text-error"
              >
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAuthIndicator;